/// <amd-module name="ClemtexPay.PayHosted.View"/>
//
// Backbone view for /pay/:slug.
//
// Flow:
//   initialize → extract slug from URL → AJAX to bb_sl_pay_resolve.js
//   → on success, load Stripe.js → mount Stripe Payment Element
//   → on submit, stripe.confirmPayment → Stripe redirects on success
//
// Single AJAX call: the resolve Suitelet does slug-resolve + HMAC verify +
// record load + PaymentIntent creation + returns the client_secret. The
// View just renders the form around it.
//
// Phase 2 MVP: ACH-only, no surcharge, no saved methods, no card option.
//
define(
    'ClemtexPay.PayHosted.View',
    [
        'Backbone',
        'jQuery',
        'clemtex_pay_payment.tpl'
    ],
    function (Backbone, jQuery, payHostedTpl) {
        'use strict';

        // ⚠️ Update if the resolve Suitelet is redeployed (script/deploy IDs
        // change, or the External URL token is rotated). The whole URL up to
        // (but not including) `&slug=` goes here.
        var RESOLVE_URL = 'https://608324.extforms.netsuite.com/app/site/hosting/scriptlet.nl?script=3188&deploy=1&compid=608324&ns-at=AAEJ7tMQicapLs-a9vp4vEmgqUs8RtUdEmLGS99k5o8qFT2fkBI';

        var STRIPE_JS_URL = 'https://js.stripe.com/v3/';

        var $ = jQuery || (window && window.jQuery);

        var PayHostedView = Backbone.View.extend({
            template: payHostedTpl,
            title: 'Payment',
            page_header: 'Payment',
            attributes: {
                id: 'clemtex-pay-page',
                'class': 'clemtex-pay-page'
            },

            events: {
                'click .clemtex-pay-submit': 'onSubmit',
                'submit .clemtex-pay-po-form': 'onPoSubmit',
                'change .clemtex-pay-po-file-input': 'onPoFileChange',
                'click .clemtex-pay-method-btn': 'onMethodChange',
                'change .clemtex-pay-save-checkbox': 'onSaveToggle',
                'change .clemtex-pay-inv-cb': 'onInvoiceToggle',
                'input .clemtex-pay-inv-amt': 'onInvoiceAmountInput',
                'blur .clemtex-pay-inv-amt': 'onInvoiceAmountBlur',
                'change .clemtex-pay-shortpay-reason': 'onShortPayReason',
                'input .clemtex-pay-shortpay-note': 'onInvoiceAmountInput',
                'change .clemtex-pay-credit-cb': 'onInvoiceAmountInput',
                'click .clemtex-pay-continue-btn': 'onContinueToPayment',
                'click .clemtex-pay-select-all': 'onSelectAllInvoices',
                'click .clemtex-pay-deselect-all': 'onDeselectAllInvoices',
                'click .clemtex-pay-skip-po-btn': 'onPayLaterClick',
                'click .clemtex-pay-rebuy-btn': 'onRebuyClick'
            },

            initialize: function () {
                this.state = 'loading';
                this.error = null;
                this.errorDetail = null;
                this.resolveData = null;
                this.stripe = null;
                this.elements = null;
                this.paymentElement = null;
                this._stripeMounted = false;
                this.paymentIntentId = null;
                this.currentClientSecret = null;
                this.selectedSavedMethod = null;
                // Re-order consent for an already-processed quote: the server
                // answers quote_processed_rebuyable until the customer clicks
                // Order Again, which sets this and re-resolves with rebuy=1.
                this.rebuyMode = false;
                this.rebuyMessage = null;

                // Strip the SC global chrome (top bar, primary nav, store
                // footer) when we're on /pay/*. CSS targets the most common SC
                // selectors when body.clemtex-pay-active is present — see
                // _clemtex_pay.scss. The class stays on body for the lifetime
                // of the page; since payment flows redirect away (or the user
                // closes the tab), lingering doesn't bite us in practice.
                if (typeof document !== 'undefined' && document.body) {
                    document.body.classList.add('clemtex-pay-active');
                }

                // Load Oswald (Clemtex brand display font) — used for headings
                // + the wordmark. Body font is Calibri/system-fallback so we
                // don't need a web-font load for that.
                if (typeof document !== 'undefined' && document.head) {
                    var hasOswald = !!document.querySelector('link[data-clemtex-pay-font]');
                    if (!hasOswald) {
                        var link = document.createElement('link');
                        link.rel = 'stylesheet';
                        link.href = 'https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&display=swap';
                        link.setAttribute('data-clemtex-pay-font', '1');
                        document.head.appendChild(link);
                    }
                }

                this._suppressChatWidget();

                // Stripe redirects to ?redirect_status=succeeded|processing|failed
                // on return from the Payment Element — with &payment_intent=pi_xxx
                // for payments, but &setup_intent=seti_xxx for save-a-method
                // setups (confirmSetup). If we see those, skip the resolve+pay
                // flow and render the outcome state instead.
                var qs = this._getUrlParams();
                // Slug must be extracted BEFORE the redirect-outcome branch:
                // _triggerInlineProcessing bails without it, and it used to be
                // set only after this branch returned — so inline processing
                // silently never ran on redirect-back and record creation
                // waited for the poller.
                this.slug = this._extractSlug();
                if (qs.redirect_status) {
                    this.paymentIntentId = qs.payment_intent || null;
                    // Setup returns saved (or failed to save) a method — no
                    // charge happened, so the payment outcome copy is wrong,
                    // and there's no PaymentIntent to trigger processing for.
                    if (qs.setup_intent || qs.setup_intent_client_secret) {
                        if (qs.redirect_status === 'succeeded') this.state = 'setup-saved';
                        else if (qs.redirect_status === 'processing') this.state = 'setup-processing';
                        else this.state = 'setup-failed';
                        this.render();
                        return;
                    }
                    if (qs.redirect_status === 'succeeded' || qs.redirect_status === 'processing') {
                        // redirect_status reports the CONFIRMATION outcome,
                        // not the payment's: a bank debit comes back
                        // 'succeeded' while the PaymentIntent is still
                        // 'processing' for 2-4 days. Render provisionally
                        // from the flag, then let process_intent's
                        // authoritative PI status correct the page (the same
                        // call posts the Customer Payment / Deposit inline so
                        // the record exists before the poller runs).
                        this.state = (qs.redirect_status === 'processing') ? 'processing-bank' : 'paid';
                        this.render();
                        if (this.paymentIntentId) {
                            var selfInit = this;
                            this._triggerInlineProcessing(this.paymentIntentId, function (status, data) {
                                // requires_action: a hand-entered bank account
                                // still needs microdeposit verification — show
                                // the steps instead of "payment received".
                                if (status === 'requires_action') {
                                    selfInit.verifyHtml = (data && data.verify_html) || '';
                                }
                                var corrected = status === 'processing' ? 'processing-bank'
                                    : status === 'succeeded' ? 'paid'
                                    : status === 'requires_action' ? 'verify-bank'
                                    : null;
                                if (corrected && corrected !== selfInit.state) {
                                    selfInit.state = corrected;
                                    selfInit.render();
                                }
                            });
                        }
                        return;
                    }
                    if (qs.redirect_status === 'failed') {
                        this.state = 'paid-failed';
                        this.render();
                        return;
                    }
                    // Unknown redirect_status — fall through to normal flow.
                }

                if (!this.slug) {
                    this._setError('no_slug', 'No payment link in the URL.');
                    return;
                }

                this.currentMethod = 'ach';
                this.currentPaymentAmountCents = 0;
                this.currentPaymentSurchargeCents = 0;

                this._resolve();
            },

            // Hide the Tawk.to chat widget. Tawk's DOM uses randomized element
            // IDs (anti-adblock) so CSS selectors can't target it, and it's
            // appended to <body> at the root stacking context so our overlay's
            // z-index can't beat it (our DOM is nested inside SC's #layout
            // container, which traps our z-index). So we call Tawk's own API.
            // Tawk may not be loaded when we run — hook onLoad + poll briefly.
            _suppressChatWidget: function () {
                var hide = function () {
                    try {
                        if (window.Tawk_API && typeof window.Tawk_API.hideWidget === 'function') {
                            window.Tawk_API.hideWidget();
                            return true;
                        }
                    } catch (e) { /* ignore */ }
                    return false;
                };
                if (hide()) return;
                try {
                    window.Tawk_API = window.Tawk_API || {};
                    var prior = window.Tawk_API.onLoad;
                    window.Tawk_API.onLoad = function () {
                        if (typeof prior === 'function') {
                            try { prior(); } catch (e) { /* ignore */ }
                        }
                        hide();
                    };
                } catch (e) { /* ignore */ }
                var tries = 0;
                var poll = setInterval(function () {
                    tries++;
                    if (hide() || tries > 30) clearInterval(poll);
                }, 400);
            },

            _getUrlParams: function () {
                var out = {};
                var qs = (window.location.search || '').replace(/^\?/, '');
                if (!qs) return out;
                var pairs = qs.split('&');
                for (var i = 0; i < pairs.length; i++) {
                    var eq = pairs[i].indexOf('=');
                    if (eq === -1) {
                        out[decodeURIComponent(pairs[i])] = '';
                    } else {
                        var k = decodeURIComponent(pairs[i].slice(0, eq));
                        var v = decodeURIComponent(pairs[i].slice(eq + 1));
                        out[k] = v;
                    }
                }
                return out;
            },

            // Default SC view render entry. Returns this so it chains.
            render: function () {
                this._renderTemplate();
                // For PO-eligible quotes the payment form is hidden behind a
                // "Pay now without submitting a PO" button. Defer Stripe
                // Element mount until that button is clicked — mounting in a
                // display:none container can cause rendering issues.
                var ctx = this.getContext();
                this._payLaterHidden = !!ctx.showPayLaterButton;
                if (this.state === 'ready' && !this._stripeMounted && !this._payLaterHidden) {
                    this._mountStripeElement();
                }
                // Multi-invoice page: sync the displayed "Selected total" to
                // the actual checkbox state on first render (checkboxes are
                // checked by default in the template, but the total label
                // starts at "$0.00 / 0 invoices" until something fires the
                // update — without this, customer sees a stale total).
                if (ctx.isCustomerKind && ctx.customerHasInvoices && !ctx.isSetupMode) {
                    this._updateCustomerSelectionTotal();
                }
                return this;
            },

            onPayLaterClick: function (e) {
                if (e) e.preventDefault();
                this._payLaterHidden = false;
                this.$('.clemtex-pay-skip-po-btn-wrap').hide();
                this.$('.clemtex-pay-form-section').show();
                if (!this._stripeMounted && this.elements) {
                    this._mountStripeElement();
                }
            },

            // Customer clicked "Order this quote again" on the rebuy-offer
            // screen — re-resolve with the consent flag; the server then
            // returns a normal payment payload for the quote's full amount.
            onRebuyClick: function (e) {
                if (e) e.preventDefault();
                this.rebuyMode = true;
                this.state = 'loading';
                this.render();
                this._resolve();
            },

            _renderTemplate: function () {
                var ctx = this.getContext();
                var html;
                if (typeof this.template === 'function') {
                    html = this.template(ctx);
                } else if (this.template && typeof this.template.render === 'function') {
                    html = this.template.render(ctx);
                } else {
                    html = String(this.template || '');
                }
                this.$el.html(html);
            },

            _extractSlug: function () {
                var fragment = String(Backbone.history.getFragment() || '');
                var pathOnly = fragment.split('?')[0];
                var segments = pathOnly.split('/').filter(function (s) {
                    return s && s.length;
                });
                if (!segments.length) return null;
                var last = segments[segments.length - 1];
                if (last.toLowerCase() === 'pay') return null;
                return /^[A-Za-z0-9]{6,12}$/.test(last) ? last : null;
            },

            _setError: function (code, detail) {
                this.state = 'error';
                this.error = code;
                this.errorDetail = detail || this._defaultErrorMessage(code);
                this.render();
            },

            _defaultErrorMessage: function (code) {
                var messages = {
                    'no_slug': 'No payment link in the URL. Please use the link you were sent.',
                    'invalid': 'This payment link is no longer valid.',
                    'not_found': 'This payment link was not found.',
                    'expired': 'This payment link has expired. Contact us for a new one.',
                    'inactive': 'This payment link has been disabled.',
                    'server_error': 'Something went wrong on our end. Please try again.',
                    'network': 'Could not reach the server. Check your internet and reload.',
                    'unsupported': "This payment type isn't supported on the new page yet.",
                    // Deliberate refusals from the resolver — the server sends
                    // its own message; these are fallbacks only.
                    'settled': 'This has already been paid in full — no payment is due.',
                    'pending_payment': 'A bank payment is already in progress. To avoid a duplicate charge, online payment is unavailable until it completes.',
                    'quote_processed': 'This quote has already been processed. If you believe a balance is still due, please contact us.'
                };
                return messages[code] || 'Something went wrong.';
            },

            // '&rebuy=1' once the customer consented to re-order a processed
            // quote — read by the server as a URL param on GET and POST.
            _rebuyParam: function () {
                return this.rebuyMode ? '&rebuy=1' : '';
            },

            _resolve: function () {
                var self = this;
                var url = RESOLVE_URL + '&slug=' + encodeURIComponent(this.slug)
                        + this._rebuyParam()
                        + '&_=' + Date.now();
                fetch(url, {
                    method: 'GET',
                    credentials: 'omit',
                    cache: 'no-store'
                })
                .then(function (response) {
                    if (!response.ok) {
                        throw new Error('HTTP ' + response.status);
                    }
                    return response.json();
                })
                .then(function (data) {
                    if (!data) {
                        return self._setError('server_error', 'Empty server response.');
                    }
                    if (!data.ok) {
                        // Already-processed quote that may be re-ordered:
                        // render the offer screen instead of a dead end.
                        if (data.error === 'quote_processed_rebuyable') {
                            self.rebuyMessage = data.message
                                || 'This quote has already been processed. You can order it again — paying will create a new, separate order for the same items and amount.';
                            self.state = 'rebuy-offer';
                            self.render();
                            return;
                        }
                        // Prefer the server's human-readable message (deliberate
                        // refusals like settled / pending_payment explain
                        // themselves); fall back to the per-code default.
                        return self._setError(data.error || 'server_error', data.message || self._defaultErrorMessage(data.error));
                    }
                    if (data.display && data.display.unsupportedInPhase2) {
                        return self._setError('unsupported', null);
                    }
                    self.resolveData = data;
                    // B5/B6: customer kind has no PI in the GET response. For
                    // mode=setup we POST create_setup_intent; for the
                    // multi-invoice flow we wait for invoice selection.
                    if (data.display && data.display.kind === 'customer') {
                        // Instance flag — onMethodChange reads this to know it
                        // must send invoice_ids with the create_intent POST.
                        self.isCustomerKind = true;
                        if (data.display.mode === 'setup') {
                            self.isSetupMode = true;
                            self._loadStripeJs();
                            return;
                        }
                        self.customerSelectedIds = {};
                        if (data.display.invoices) {
                            for (var i = 0; i < data.display.invoices.length; i++) {
                                var inv = data.display.invoices[i];
                                if (!inv.pending) self.customerSelectedIds[inv.id] = true;
                            }
                        }
                        self._loadStripeJs();
                        return;
                    }
                    if (!data.payment || !data.payment.client_secret || !data.payment.publishable_key) {
                        return self._setError('server_error', 'Missing payment details from server.');
                    }
                    self._loadStripeJs();
                })
                .catch(function (e) {
                    self._setError('network', 'Could not reach the server: ' + ((e && e.message) || 'unknown'));
                });
            },

            _loadStripeJs: function () {
                var self = this;
                if (window.Stripe) {
                    return self._initStripe();
                }
                var script = document.createElement('script');
                script.src = STRIPE_JS_URL;
                script.async = true;
                script.onload = function () { self._initStripe(); };
                script.onerror = function () { self._setError('server_error', 'Could not load the payment library.'); };
                document.head.appendChild(script);
            },

            _initStripe: function () {
                var pay = this.resolveData.payment;
                // B6: setup mode — POST create_setup_intent to get the
                // SetupIntent client_secret, then mount Element with it.
                if (this.isSetupMode) {
                    return this._initStripeForSetup();
                }
                // B5: kind=customer has no PI yet (created after invoice
                // selection). Just go to ready state — Stripe init happens
                // later via onContinueToPayment.
                if (!pay) {
                    this.state = 'ready';
                    this.render();
                    return;
                }
                try {
                    this.stripe = window.Stripe(pay.publishable_key);
                    this.elements = this.stripe.elements(this._elementsOptions(pay));
                    this.currentPaymentAmountCents = pay.amount_cents || 0;
                    this.currentPaymentSurchargeCents = 0; // initial PI is ACH, no surcharge
                } catch (e) {
                    return this._setError('server_error', 'Could not initialize the payment library.');
                }
                this.state = 'ready';
                this.render();
            },

            // Build the stripe.elements() options. Including
            // customerSessionClientSecret makes the Payment Element show the
            // customer's saved payment methods + (for automatic-method flows)
            // a native "save this method" checkbox.
            _elementsOptions: function (pay) {
                // Choke point for every PAYMENT elements build (setup mode
                // builds its options inline) — remember the PI's client
                // secret so _confirmSavedBank can derive the pi_xxx id.
                this.currentClientSecret = pay.client_secret || null;
                var opts = {
                    clientSecret: pay.client_secret,
                    appearance: { theme: 'stripe' }
                };
                if (pay.customer_session_client_secret) {
                    opts.customerSessionClientSecret = pay.customer_session_client_secret;
                }
                return opts;
            },

            _initStripeForSetup: function () {
                var self = this;
                var url = RESOLVE_URL
                    + '&slug=' + encodeURIComponent(this.slug)
                    + '&action=create_setup_intent';
                fetch(url, {
                    method: 'POST',
                    credentials: 'omit',
                    cache: 'no-store',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: ''
                })
                .then(function (r) {
                    if (!r.ok) throw new Error('HTTP ' + r.status);
                    return r.json();
                })
                .then(function (data) {
                    if (!data || !data.ok || !data.payment) {
                        throw new Error((data && data.error) || 'setup_intent_failed');
                    }
                    try {
                        self.stripe = window.Stripe(data.payment.publishable_key);
                        self.elements = self.stripe.elements({
                            clientSecret: data.payment.client_secret,
                            appearance: { theme: 'stripe' }
                        });
                    } catch (initErr) {
                        throw new Error('Could not initialize payment library.');
                    }
                    self.state = 'ready';
                    self.render();
                })
                .catch(function (e) {
                    self._setError('server_error', 'Could not start setup: ' + ((e && e.message) || 'unknown'));
                });
            },

            _mountStripeElement: function () {
                if (!this.elements) return;
                var mountPoint = this.$('#clemtex-pay-stripe-element')[0];
                if (!mountPoint) return;
                try {
                    this.paymentElement = this.elements.create('payment', { layout: 'tabs' });
                    this.paymentElement.mount(mountPoint);
                    this._trackSavedMethodSelection();
                    this._stripeMounted = true;
                } catch (e) {
                    this._setError('server_error', 'Could not load the payment form.');
                }
            },

            // event.value.payment_method is only populated when a SAVED
            // method (customer-session redisplay) is selected — null for the
            // new-method tabs. onSubmit uses this to route saved bank
            // accounts through the server-side confirm.
            _trackSavedMethodSelection: function () {
                var self = this;
                this.selectedSavedMethod = null;
                if (!this.paymentElement) return;
                this.paymentElement.on('change', function (event) {
                    self.selectedSavedMethod =
                        (event && event.value && event.value.payment_method) || null;
                });
            },

            onSubmit: function (e) {
                if (e) e.preventDefault();
                if (!this.stripe || !this.elements || this.state !== 'ready') return;

                // Disable button + clear inline error WITHOUT re-rendering
                // (re-rendering would destroy the mounted Stripe Element).
                var $btn = this.$('.clemtex-pay-submit');
                var originalLabel = $btn.text();
                $btn.prop('disabled', true).text('Processing…');
                this.$('.clemtex-pay-submit-error').hide().text('');

                // Saved bank accounts confirm SERVER-SIDE (off-session),
                // reusing the mandate collected when the account was saved.
                // Confirming through the Element re-collects a mandate on
                // every payment, and Stripe emails the customer a "signed up
                // for direct debits" mandate confirmation each time.
                var saved = this.selectedSavedMethod;
                if (!this.isSetupMode && saved && saved.id && saved.type === 'us_bank_account') {
                    return this._confirmSavedBank(saved.id, $btn, originalLabel);
                }
                this._confirmViaElement($btn, originalLabel);
            },

            _confirmViaElement: function ($btn, originalLabel) {
                var self = this;
                var returnUrl = window.location.origin + window.location.pathname + '?paid=1';

                // B6: setup mode uses confirmSetup (no charge, just saves
                // the method against the customer).
                var confirmCall = this.isSetupMode
                    ? this.stripe.confirmSetup({
                        elements: this.elements,
                        confirmParams: { return_url: returnUrl }
                    })
                    : this.stripe.confirmPayment({
                        elements: this.elements,
                        confirmParams: { return_url: returnUrl }
                    });

                confirmCall.then(function (result) {
                    if (result && result.error) {
                        self.$('.clemtex-pay-submit-error')
                            .text(result.error.message || (self.isSetupMode ? 'Could not save payment method.' : 'Payment was declined.'))
                            .show();
                        $btn.prop('disabled', false).text(originalLabel);
                    }
                    // On success Stripe redirects to return_url.
                });
            },

            _confirmSavedBank: function (pmId, $btn, originalLabel) {
                var self = this;
                var cs = this.currentClientSecret || '';
                var intentId = cs.indexOf('_secret') > 0 ? cs.split('_secret')[0] : null;
                if (!this.slug || !intentId) {
                    // Can't identify the PI server-side — the Element confirm
                    // still works (the mandate email fires, but the payment
                    // goes through).
                    return this._confirmViaElement($btn, originalLabel);
                }
                var url = RESOLVE_URL
                    + '&slug=' + encodeURIComponent(this.slug)
                    + '&action=confirm_saved';
                var body = 'payment_intent_id=' + encodeURIComponent(intentId)
                    + '&payment_method_id=' + encodeURIComponent(pmId);
                fetch(url, {
                    method: 'POST',
                    credentials: 'omit',
                    cache: 'no-store',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: body
                })
                .then(function (r) { return r.json(); })
                .then(function (data) {
                    if (data && data.ok && data.status) {
                        // Server already ran inline processing (records +
                        // customer emails) — just render the outcome. No
                        // redirect happens on this path.
                        self.paymentIntentId = data.payment_intent_id || intentId;
                        self.state = (data.status === 'succeeded') ? 'paid' : 'processing-bank';
                        self.render();
                        return;
                    }
                    try { console.warn('[ClemtexPay] confirm_saved failed — falling back to Element confirm', data); } catch (_) {}
                    self._confirmViaElement($btn, originalLabel);
                })
                .catch(function (err) {
                    try { console.warn('[ClemtexPay] confirm_saved network error — falling back to Element confirm', err); } catch (_) {}
                    self._confirmViaElement($btn, originalLabel);
                });
            },

            getContext: function () {
                var display = (this.resolveData && this.resolveData.display) || {};
                var header = display.header || {};
                var rawLineItems = display.lineItems || [];
                var amountDisplay = display.amountDisplay || '';
                var customer = display.customer || {};

                // Filter + format line items. printitems = false/'F' means
                // the NetSuite user opted to hide the line on print/customer
                // views; respect that.
                var lineItems = [];
                for (var i = 0; i < rawLineItems.length; i++) {
                    var li = rawLineItems[i] || {};
                    if (li.printitems === false || li.printitems === 'F') continue;
                    lineItems.push({
                        item: li.item || '',
                        quantity: this._formatQty(li.quantity),
                        description: li.description || '',
                        rateDisplay: this._formatUsd(Math.round((li.rate || 0) * 100)),
                        amountDisplay: this._formatUsd(Math.round((li.amount || 0) * 100)),
                        oldPartNumber: li.oldPartNumber || ''
                    });
                }

                var totalCents = Math.round((header.total || 0) * 100);
                var balanceCents = display.amountCents || 0;
                var hasBalanceDelta = totalCents !== balanceCents && balanceCents > 0;

                return {
                    state: this.state,
                    isLoading: this.state === 'loading',
                    isReady: this.state === 'ready',
                    isError: this.state === 'error',
                    isPaid: this.state === 'paid',
                    isProcessingBank: this.state === 'processing-bank',
                    isVerifyBank: this.state === 'verify-bank',
                    verifyHtml: this.verifyHtml || '',
                    isPaidFailed: this.state === 'paid-failed',
                    isSetupSaved: this.state === 'setup-saved',
                    isSetupProcessing: this.state === 'setup-processing',
                    isSetupFailed: this.state === 'setup-failed',
                    isRebuyOffer: this.state === 'rebuy-offer',
                    rebuyMessage: this.rebuyMessage || '',
                    isRebuyMode: !!this.rebuyMode,
                    error: this.error,
                    errorDetail: this.errorDetail,
                    paymentIntentId: this.paymentIntentId,
                    companyName: display.companyName || 'Clemtex',
                    supportEmail: display.supportEmail || 'ar_support@clemtex.com',
                    description: display.description || '',
                    docNoun: display.docNoun || '',
                    docId: display.docId || '',
                    amountDisplay: amountDisplay,
                    payButtonLabel: amountDisplay ? 'Pay ' + amountDisplay : 'Pay',
                    customerName: customer.name || '',
                    customerEmail: customer.email || '',
                    customerPhone: customer.phone || '',
                    paymentMethodLabel: 'ACH bank transfer',
                    // B2: rich record data for line items table + addresses +
                    // metadata + totals (matching the existing Suitelet layout)
                    trandate: header.trandate || '',
                    billAddress: header.billaddress || '',
                    shipAddress: header.shipaddress || '',
                    hasBillAddress: !!header.billaddress,
                    hasShipAddress: !!header.shipaddress,
                    metaRow: {
                        fob: header.fob || '',
                        freight: header.freight || '',
                        shipmethod: header.shipmethod || '',
                        salesrep: header.salesrep || '',
                        hasFob: !!header.fob,
                        hasFreight: !!header.freight,
                        hasShipmethod: !!header.shipmethod,
                        hasSalesrep: !!header.salesrep,
                        hasAny: !!(header.fob || header.freight || header.shipmethod || header.salesrep)
                    },
                    orderedBy: {
                        name: header.orderedByName || '',
                        email: header.orderedByEmail || '',
                        phone: header.orderedByPhone || '',
                        createdBy: header.createdby || '',
                        hasAny: !!(header.orderedByName || header.orderedByEmail || header.orderedByPhone || header.createdby)
                    },
                    lineItems: lineItems,
                    hasLineItems: lineItems.length > 0,
                    showPoSection: display.kind === 'estimate'
                        && display.eligibility
                        && display.eligibility.eligible === true,
                    showPayLaterButton: display.kind === 'estimate'
                        && display.eligibility
                        && display.eligibility.eligible === true,
                    poEligibility: display.eligibility || null,
                    // AR mailbox for credit-limit / on-hold notices.
                    // Separate from supportEmail (AR@clemtex.com) — these
                    // notices specifically need the AR Support inbox where
                    // credit-limit changes are handled.
                    arSupportEmail: 'ar_support@clemtex.com',
                    // Notice shown ONLY when there's a customer-actionable
                    // reason PO isn't available (so they know to contact us).
                    // `no_terms` is silent — customer just doesn't have terms,
                    // no notice needed. Internal-error reasons also silent.
                    showPoNotEligibleNotice: display.kind === 'estimate'
                        && display.eligibility
                        && display.eligibility.eligible === false
                        && (display.eligibility.reason === 'on_credit_hold'
                            || display.eligibility.reason === 'would_exceed_credit_limit'),
                    poNoticeIsOnCreditHold: display.eligibility && display.eligibility.reason === 'on_credit_hold',
                    poNoticeWouldExceedLimit: display.eligibility && display.eligibility.reason === 'would_exceed_credit_limit',
                    // B4: method selector — only for invoices where surcharge
                    // applies (those have an actual choice between ACH no-fee
                    // and Card +fee). Estimate/SO use automatic methods on the
                    // Stripe Element directly, no explicit selector needed.
                    // B5/B6: customer kind data
                    isCustomerKind: display.kind === 'customer',
                    isSetupMode: display.kind === 'customer' && display.mode === 'setup',
                    customerInvoices: (display.invoices || []).map(function (inv) {
                        return {
                            id: inv.id,
                            tranid: inv.tranid,
                            trandate: inv.trandate,
                            duedate: inv.duedate,
                            amountremainingDisplay: inv.amountremainingDisplay,
                            amountremainingCents: inv.amountremainingCents,
                            amountremainingInput: ((inv.amountremainingCents || 0) / 100).toFixed(2),
                            taxCents: inv.taxCents || 0,
                            poNumber: inv.poNumber,
                            pending: inv.pending,
                            defaultChecked: !inv.pending
                        };
                    }),
                    customerHasInvoices: (display.invoices || []).length > 0,
                    // Credits on account — opt-in (unticked), applied in list
                    // order up to the amount being paid.
                    customerCredits: display.credits || [],
                    customerHasCredits: (display.credits || []).length > 0,
                    customerShortPayReasons: display.shortPayReasons || [],
                    customerShortPayNoteMax: display.shortPayNoteMax || 150,
                    isCreditsApplied: this.state === 'credits-applied',
                    creditsAppliedDisplay: this._formatUsd(this.creditsAppliedCents || 0),
                    customerTotalOpenDisplay: display.totalOpenDisplay || '',
                    // Base URL the template uses to build per-invoice PDF
                    // links — each table row appends &invoice_id={id}.
                    customerInvoicePdfUrlBase: (this.slug && display.kind === 'customer')
                        ? RESOLVE_URL + '&slug=' + encodeURIComponent(this.slug) + '&action=invoice_pdf'
                        : '',
                    // Method selector shows for: single invoices with a
                    // surcharge-eligible config, AND every multi-invoice
                    // (kind=customer) page — multi-invoice customers can pay
                    // by card too, and card on invoices carries the 3% fee.
                    showMethodSelector: (display.kind === 'invoice'
                            && display.surcharge
                            && display.surcharge.applies === true)
                        || (display.kind === 'customer'
                            && display.mode !== 'setup'
                            && (display.invoices || []).length > 0),
                    // Card-button fee label: exact for single invoices
                    // (pre-computed), generic for multi-invoice (exact amount
                    // appears once the card PI is created, since it depends on
                    // the selected invoice set).
                    // Statement page: "no fee" when none of the payable
                    // invoices carry the surcharge (invoice or customer opted
                    // out); re-evaluated for the actual selection on Continue.
                    methodCardFeeLabel: (display.kind === 'invoice'
                            && display.surcharge && display.surcharge.display)
                        ? '+' + display.surcharge.display + ' fee'
                        : (display.kind === 'customer'
                            ? this._cardFeeLabelFor(display.invoices || [])
                            : '+ 3% card fee'),
                    surcharge: {
                        applies: !!(display.surcharge && display.surcharge.applies),
                        display: (display.surcharge && display.surcharge.display) || '',
                        ratePercent: (display.surcharge && display.surcharge.rateBps)
                            ? (display.surcharge.rateBps / 100).toFixed(1) + '%'
                            : '3.0%',
                        totalWithCardDisplay: (display.surcharge && display.surcharge.totalWithCardDisplay) || ''
                    },
                    totals: {
                        subtotalDisplay: this._formatUsd(Math.round((header.subtotal || 0) * 100)),
                        taxtotalDisplay: this._formatUsd(Math.round((header.taxtotal || 0) * 100)),
                        shippingcostDisplay: this._formatUsd(Math.round((header.shippingcost || 0) * 100)),
                        totalDisplay: this._formatUsd(totalCents),
                        balanceDisplay: amountDisplay,
                        hasSubtotal: header.subtotal > 0,
                        hasTax: header.taxtotal > 0,
                        hasShipping: header.shippingcost > 0,
                        showBalance: hasBalanceDelta
                    }
                };
            },

            _formatUsd: function (cents) {
                var dollars = (cents || 0) / 100;
                return '$' + dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            },

            _formatQty: function (q) {
                if (q == null) return '';
                // Drop trailing .00 on whole numbers (so "1.00" → "1")
                var n = Number(q);
                if (isNaN(n)) return String(q);
                return n % 1 === 0 ? String(Math.round(n)) : n.toFixed(2);
            },

            // ─── B3: PO submission ───

            onPoFileChange: function (e) {
                // Clear any prior error when user picks a new file.
                this.$('.clemtex-pay-po-error').hide().text('');
            },

            onPoSubmit: function (e) {
                if (e) e.preventDefault();
                var self = this;
                var $form = this.$('.clemtex-pay-po-form');
                var $err = this.$('.clemtex-pay-po-error');
                var $btn = this.$('.clemtex-pay-po-submit');

                var poNumber = String(this.$('.clemtex-pay-po-number-input').val() || '').trim();
                var fileInput = this.$('.clemtex-pay-po-file-input')[0];
                var file = fileInput && fileInput.files && fileInput.files[0];

                $err.hide().text('');

                if (!poNumber) {
                    $err.text('PO number is required.').show();
                    return;
                }
                if (!file) {
                    $err.text('Please attach a PO PDF.').show();
                    return;
                }
                if (file.type && file.type !== 'application/pdf') {
                    $err.text('File must be a PDF.').show();
                    return;
                }
                if (file.size && file.size > 5 * 1024 * 1024) {
                    $err.text('PDF exceeds the 5 MB upload limit.').show();
                    return;
                }

                var originalLabel = $btn.text();
                $btn.prop('disabled', true).html(
                    '<span class="clemtex-pay-btn-spinner" aria-hidden="true"></span>Submitting…'
                );

                this._fileToBase64(file, function (err, b64) {
                    if (err) {
                        $err.text(err.message || 'Could not read the PDF.').show();
                        $btn.prop('disabled', false).text(originalLabel);
                        return;
                    }
                    self._submitPo(poNumber, b64, file.name || 'po.pdf',
                        function onSuccess(data) {
                            // Replace the form with a success panel (direct DOM
                            // — no full re-render, preserves the rest of page +
                            // the mounted Stripe element below).
                            self.$('.clemtex-pay-po-form').hide();
                            // Prefer the customer-facing tranid over the
                            // internal id — internal ids are NetSuite-internal
                            // and meaningless to customers.
                            var soLabel = '';
                            if (data.salesOrderTranid) {
                                soLabel = '#' + data.salesOrderTranid;
                            } else if (data.salesOrderId) {
                                soLabel = '(ref ' + data.salesOrderId + ')';
                            }
                            self.$('.clemtex-pay-po-success')
                                .show()
                                .find('.clemtex-pay-po-so-id')
                                .text(soLabel);
                            // Hide the "or pay now" divider + payment form too,
                            // since payment isn't needed once PO is accepted.
                            self.$('.clemtex-pay-or-divider').hide();
                            self.$('.clemtex-pay-skip-po-btn-wrap').hide();
                            self.$('.clemtex-pay-form-section').hide();
                        },
                        function onError(msg) {
                            $err.text(msg || 'Could not submit the PO. Please try again or contact us.').show();
                            $btn.prop('disabled', false).text(originalLabel);
                        }
                    );
                });
            },

            _fileToBase64: function (file, callback) {
                var reader = new FileReader();
                reader.onload = function () {
                    var result = String(reader.result || '');
                    var commaIdx = result.indexOf(',');
                    if (commaIdx === -1) return callback(new Error('Could not read PDF.'), null);
                    callback(null, result.slice(commaIdx + 1));
                };
                reader.onerror = function () {
                    callback(new Error('Could not read PDF.'), null);
                };
                reader.readAsDataURL(file);
            },

            // ─── B4: method swap (ACH ↔ Card) ───

            onMethodChange: function (e) {
                if (e) e.preventDefault();
                var $btn = this.$(e.currentTarget);
                var method = String($btn.attr('data-method') || '').toLowerCase();
                if (method !== 'ach' && method !== 'card') return;
                if (method === this.currentMethod) return;
                this._recreateIntent(method);
            },

            // Save-consent checkbox. setup_future_usage has to be baked into
            // the PI before confirm, so toggling the box re-creates the PI
            // for the current method with the new save_for_future flag.
            onSaveToggle: function () {
                this._recreateIntent(this.currentMethod);
            },

            _saveForFuture: function () {
                var cb = this.$('.clemtex-pay-save-checkbox');
                return !!(cb.length && cb[0].checked);
            },

            _recreateIntent: function (method) {
                var self = this;
                // Disable both method buttons + show pending state
                var $btns = this.$('.clemtex-pay-method-btn');
                $btns.prop('disabled', true);
                this.$('.clemtex-pay-submit').prop('disabled', true);

                var url = RESOLVE_URL
                    + '&slug=' + encodeURIComponent(this.slug)
                    + '&action=create_intent'
                    + this._rebuyParam();
                var body = 'method=' + encodeURIComponent(method)
                    + '&save_for_future=' + (this._saveForFuture() ? 'true' : 'false');
                // Multi-invoice: create_intent needs the selected invoice ids
                // to total + (for card) compute the surcharge.
                if (this.isCustomerKind) {
                    body += this._customerBody || '';
                }

                fetch(url, {
                    method: 'POST',
                    credentials: 'omit',
                    cache: 'no-store',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: body
                })
                .then(function (response) {
                    if (!response.ok) throw new Error('HTTP ' + response.status);
                    return response.json();
                })
                .then(function (data) {
                    if (!data || !data.ok || !data.payment) {
                        throw new Error((data && data.error) || 'method_swap_failed');
                    }
                    self.currentMethod = data.method;
                    self.currentPaymentAmountCents = data.payment.amount_cents;
                    self.currentPaymentSurchargeCents = data.payment.surcharge_cents || 0;
                    self._remountStripeWithPayment(data.payment);
                    self._updateMethodUI();
                    $btns.prop('disabled', false);
                    self.$('.clemtex-pay-submit').prop('disabled', false);
                })
                .catch(function (e) {
                    $btns.prop('disabled', false);
                    self.$('.clemtex-pay-submit').prop('disabled', false);
                    self.$('.clemtex-pay-submit-error')
                        .text('Could not switch payment method: ' + ((e && e.message) || 'unknown'))
                        .show();
                });
            },

            _remountStripeWithPayment: function (payment) {
                // Stripe.js doesn't let you swap clientSecret on an existing
                // elements instance — you have to create a fresh one.
                if (this.paymentElement) {
                    try { this.paymentElement.unmount(); } catch (_) { /* ignore */ }
                    this.paymentElement = null;
                }
                this.elements = this.stripe.elements(this._elementsOptions(payment));
                var mountPoint = this.$('#clemtex-pay-stripe-element')[0];
                if (!mountPoint) return;
                this.paymentElement = this.elements.create('payment', { layout: 'tabs' });
                this.paymentElement.mount(mountPoint);
                this._trackSavedMethodSelection();
            },

            _updateMethodUI: function () {
                // Highlight the active method button
                this.$('.clemtex-pay-method-btn').removeClass('clemtex-pay-method-btn-active');
                this.$('.clemtex-pay-method-btn[data-method="' + this.currentMethod + '"]')
                    .addClass('clemtex-pay-method-btn-active');

                // Update displayed total + Pay button label + method note + surcharge row
                var totalDisplay = this._formatUsd(this.currentPaymentAmountCents);
                this.$('.clemtex-pay-submit').text('Pay ' + totalDisplay);
                this.$('.clemtex-pay-totals-balance span').last().text(totalDisplay);

                if (this.currentPaymentSurchargeCents > 0) {
                    this.$('.clemtex-pay-surcharge-row').show();
                    this.$('.clemtex-pay-method-note').text(
                        'Pay by credit card. A ' +
                        this._formatUsd(this.currentPaymentSurchargeCents) +
                        ' card processing fee is included in the total.'
                    );
                } else {
                    this.$('.clemtex-pay-surcharge-row').hide();
                    this.$('.clemtex-pay-method-note').text(
                        this.currentMethod === 'card'
                            ? "Pay by credit card. No fee."
                            : "Pay by ACH bank transfer. No fee. You'll connect your bank below."
                    );
                }

                this.$('.clemtex-pay-submit-error').hide().text('');
            },

            // ─── B5: multi-invoice (kind=customer) ───

            onInvoiceToggle: function (e) {
                var cb = e && e.currentTarget;
                if (!cb) return;
                var id = String(cb.getAttribute('data-id') || '');
                if (cb.checked) {
                    this.customerSelectedIds[id] = true;
                } else {
                    delete this.customerSelectedIds[id];
                }
                this._syncInvoiceRow(id, cb.checked);
                this._updateCustomerSelectionTotal();
            },

            onSelectAllInvoices: function (e) {
                if (e) e.preventDefault();
                var self = this;
                this.$('.clemtex-pay-inv-cb').each(function () {
                    if (this.disabled) return;
                    this.checked = true;
                    var id = String(this.getAttribute('data-id') || '');
                    self.customerSelectedIds[id] = true;
                    self._syncInvoiceRow(id, true);
                });
                this._updateCustomerSelectionTotal();
            },

            onDeselectAllInvoices: function (e) {
                if (e) e.preventDefault();
                var self = this;
                this.$('.clemtex-pay-inv-cb').each(function () {
                    if (this.disabled) return;
                    this.checked = false;
                    var id = String(this.getAttribute('data-id') || '');
                    delete self.customerSelectedIds[id];
                    self._syncInvoiceRow(id, false);
                });
                this._updateCustomerSelectionTotal();
            },

            // A ticked invoice's amount is editable; unticking resets it to
            // the full balance and clears any short-pay reason.
            _syncInvoiceRow: function (id, checked) {
                var $amt = this.$('.clemtex-pay-inv-amt[data-id="' + id + '"]');
                $amt.prop('disabled', !checked);
                if (!checked) {
                    var bal = parseInt($amt.attr('data-balance-cents') || '0', 10);
                    $amt.val((bal / 100).toFixed(2));
                    this.$('.clemtex-pay-shortpay-reason[data-id="' + id + '"]').val('');
                    this.$('.clemtex-pay-shortpay-note[data-id="' + id + '"]').val('');
                }
            },

            onInvoiceAmountInput: function () {
                this.$('.clemtex-pay-customer-error').hide().text('');
                this._updateCustomerSelectionTotal();
            },

            // Normalise what was typed ("20" → "20.00") once the field loses
            // focus; leave unparseable input alone so the error can name it.
            onInvoiceAmountBlur: function (e) {
                var el = e && e.currentTarget;
                if (!el) return;
                var cents = this._parseCents(el.value);
                if (cents !== null) el.value = (cents / 100).toFixed(2);
                this._updateCustomerSelectionTotal();
            },

            // "Removing sales tax" fills in balance − tax when the invoice is
            // untouched (tax is only sent while nothing has been paid on it).
            onShortPayReason: function (e) {
                var sel = e && e.currentTarget;
                if (!sel) return;
                var id = String(sel.getAttribute('data-id') || '');
                if (sel.value === 'tax') {
                    var $amt = this.$('.clemtex-pay-inv-amt[data-id="' + id + '"]');
                    var bal = parseInt($amt.attr('data-balance-cents') || '0', 10);
                    var tax = parseInt($amt.attr('data-tax-cents') || '0', 10);
                    if (tax > 0 && tax < bal) $amt.val(((bal - tax) / 100).toFixed(2));
                }
                this.$('.clemtex-pay-customer-error').hide().text('');
                this._updateCustomerSelectionTotal();
            },

            _parseCents: function (raw) {
                var v = String(raw == null ? '' : raw).replace(/[$,\s]/g, '');
                if (!v || v === '.' || !/^\d*(\.\d{0,2})?$/.test(v)) return null;
                return Math.round(parseFloat(v) * 100);
            },

            // Reads the whole customer selection off the page: invoice lines
            // (amount + short-pay reason), the ticked credits applied in list
            // order up to the invoice total, and any problems to fix first.
            // The server re-validates all of it — this is for the display.
            _customerSelection: function () {
                var self = this;
                var lines = [];
                var errors = [];
                var invoiceCents = 0;
                this.$('.clemtex-pay-inv-cb:checked').each(function () {
                    var id = String(this.getAttribute('data-id') || '');
                    var $amt = self.$('.clemtex-pay-inv-amt[data-id="' + id + '"]');
                    var bal = parseInt(this.getAttribute('data-amount-cents') || '0', 10);
                    var tranid = $amt.attr('data-tranid') || id;
                    var cents = $amt.length ? self._parseCents($amt.val()) : bal;
                    var line = { id: id, cents: cents };
                    var short = false;
                    if (cents === null || cents <= 0) {
                        errors.push('Enter an amount to pay on ' + tranid + '.');
                    } else if (cents > bal) {
                        errors.push('The amount for ' + tranid + ' is more than its ' + self._formatUsd(bal) + ' balance.');
                    } else {
                        invoiceCents += cents;
                        if (cents < bal) {
                            short = true;
                            var reason = self.$('.clemtex-pay-shortpay-reason[data-id="' + id + '"]').val() || '';
                            var note = String(self.$('.clemtex-pay-shortpay-note[data-id="' + id + '"]').val() || '').trim();
                            if (!reason) errors.push('Choose a reason for paying less than the full balance on ' + tranid + '.');
                            else if (reason === 'other' && !note) errors.push('Add a note explaining the partial payment on ' + tranid + '.');
                            line.reason = reason;
                            line.note = note;
                        }
                    }
                    self.$('.clemtex-pay-shortpay-row[data-id="' + id + '"]').toggle(short);
                    lines.push(line);
                });
                // Rows for unticked invoices never need a reason.
                this.$('.clemtex-pay-inv-cb:not(:checked)').each(function () {
                    self.$('.clemtex-pay-shortpay-row[data-id="' + this.getAttribute('data-id') + '"]').hide();
                });

                var credits = [];
                var creditCents = 0;
                var room = invoiceCents;
                this.$('.clemtex-pay-credit-cb').each(function () {
                    var id = String(this.getAttribute('data-id') || '');
                    var avail = parseInt(this.getAttribute('data-amount-cents') || '0', 10);
                    var applying = 0;
                    if (this.checked && room > 0) {
                        applying = Math.min(avail, room);
                        room -= applying;
                        credits.push({ id: id, cents: applying });
                        creditCents += applying;
                    }
                    self.$('.clemtex-pay-credit-applying[data-id="' + id + '"]').text(
                        !this.checked ? '—' : (applying > 0 ? '−' + self._formatUsd(applying) : 'not needed'));
                });
                return {
                    lines: lines, credits: credits, errors: errors,
                    invoiceCents: invoiceCents, creditCents: creditCents,
                    netCents: invoiceCents - creditCents
                };
            },

            _updateCustomerSelectionTotal: function () {
                var sel = this._customerSelection();
                var count = sel.lines.length;
                this.$('.clemtex-pay-customer-invoices-total').text(this._formatUsd(sel.invoiceCents));
                this.$('.clemtex-pay-customer-credits-total').text('−' + this._formatUsd(sel.creditCents));
                this.$('.clemtex-pay-customer-selected-total').text(this._formatUsd(sel.netCents));
                this.$('.clemtex-pay-customer-selected-count').text(count + (count === 1 ? ' invoice' : ' invoices'));
                var creditsOnly = sel.creditCents > 0 && sel.netCents === 0;
                this.$('.clemtex-pay-continue-btn')
                    .text(creditsOnly ? 'Apply Credits' : 'Continue to Payment')
                    .prop('disabled', !count);
                this._customerSelectedTotalCents = sel.netCents;
                return sel;
            },

            // Locks the selection once a PaymentIntent exists for it — the PI
            // amount and metadata are fixed, so nothing on the page may drift.
            // Card-button sub-label for a set of invoices: the server's
            // allowsSurcharge already folds in both the invoice and the
            // customer opt-out. The fee is all-or-nothing per charge, so any
            // surchargeable invoice means the card total carries it.
            _cardFeeLabelFor: function (invoices) {
                var payable = (invoices || []).filter(function (inv) { return inv && !inv.pending; });
                if (!payable.length) return '+ 3% card fee';
                var any = payable.some(function (inv) { return inv.allowsSurcharge !== false; });
                return any ? '+ 3% card fee' : 'no fee';
            },

            _lockCustomerSelection: function () {
                // Label for exactly what is being paid now.
                var all = ((this.resolveData && this.resolveData.display) || {}).invoices || [];
                var sel = this.customerSelectedIds || {};
                var picked = all.filter(function (inv) { return sel[inv.id]; });
                if (picked.length) {
                    this.$('.clemtex-pay-method-btn[data-method="card"] .clemtex-pay-method-btn-fee')
                        .text(this._cardFeeLabelFor(picked));
                }
                this.$('.clemtex-pay-customer-invoice-section').addClass('clemtex-pay-locked');
                this.$('.clemtex-pay-inv-cb, .clemtex-pay-inv-amt, .clemtex-pay-shortpay-reason, .clemtex-pay-shortpay-note, .clemtex-pay-credit-cb, .clemtex-pay-select-all, .clemtex-pay-deselect-all')
                    .prop('disabled', true);
                this.$('.clemtex-pay-continue-btn').hide();
            },

            _customerBodyFor: function (sel) {
                return '&lines=' + encodeURIComponent(JSON.stringify(sel.lines.map(function (l) {
                        var o = { id: l.id, cents: l.cents };
                        if (l.reason) { o.reason = l.reason; o.note = l.note || ''; }
                        return o;
                    })))
                    + '&credits=' + encodeURIComponent(JSON.stringify(sel.credits));
            },

            onContinueToPayment: function (e) {
                if (e) e.preventDefault();
                var self = this;
                var sel = this._updateCustomerSelectionTotal();
                var $err = this.$('.clemtex-pay-customer-error');
                if (!sel.lines.length) return;
                if (sel.errors.length) {
                    $err.empty().show();
                    sel.errors.forEach(function (m) { $err.append(jQuery('<div>').text(m)); });
                    return;
                }
                if (sel.creditCents > 0 && sel.netCents === 0) {
                    return this._applyCreditsOnly(sel);
                }
                var minCents = ((this.resolveData && this.resolveData.display) || {}).minChargeCents || 50;
                if (sel.netCents < minCents) {
                    $err.text('The amount to pay after credits is ' + this._formatUsd(sel.netCents)
                        + '. Online payments must be at least ' + this._formatUsd(minCents)
                        + ' — add an invoice or untick a credit.').show();
                    return;
                }

                var $btn = this.$('.clemtex-pay-continue-btn');
                var originalLabel = $btn.text();
                $btn.prop('disabled', true).text('Loading payment…');

                // Kept for _recreateIntent: a method switch must re-send the
                // exact same selection, not re-read the (locked) page.
                this._customerBody = this._customerBodyFor(sel);
                var url = RESOLVE_URL
                    + '&slug=' + encodeURIComponent(this.slug)
                    + '&action=create_intent';
                var body = 'method=ach'
                    + '&save_for_future=' + (this._saveForFuture() ? 'true' : 'false')
                    + this._customerBody;

                fetch(url, {
                    method: 'POST',
                    credentials: 'omit',
                    cache: 'no-store',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: body
                })
                .then(function (response) {
                    if (!response.ok) throw new Error('HTTP ' + response.status);
                    return response.json();
                })
                .then(function (data) {
                    if (!data || !data.ok || !data.payment) {
                        throw new Error((data && data.error) || 'create_intent_failed');
                    }
                    // Stash + init Stripe
                    self.currentMethod = data.method || 'ach';
                    self.currentPaymentAmountCents = data.payment.amount_cents;
                    self.currentPaymentSurchargeCents = data.payment.surcharge_cents || 0;
                    try {
                        if (!self.stripe) self.stripe = window.Stripe(data.payment.publishable_key);
                        self.elements = self.stripe.elements(self._elementsOptions(data.payment));
                    } catch (initErr) {
                        throw new Error('Could not initialize payment library.');
                    }
                    // Reveal payment form + freeze the selection
                    self._lockCustomerSelection();
                    self.$('.clemtex-pay-form-section').show();
                    self._stripeMounted = false;
                    self._mountStripeElement();
                    // Update pay button label
                    self.$('.clemtex-pay-submit').text('Pay ' + self._formatUsd(self.currentPaymentAmountCents));
                })
                .catch(function (e) {
                    $btn.prop('disabled', false).text(originalLabel);
                    $err.text('Could not start payment: ' + ((e && e.message) || 'unknown')).show();
                });
            },

            // Net-zero selection: the credits cover it, so no Stripe charge —
            // the server posts a $0 Customer Payment applying them.
            _applyCreditsOnly: function (sel) {
                var self = this;
                var $btn = this.$('.clemtex-pay-continue-btn');
                var $err = this.$('.clemtex-pay-customer-error');
                $btn.prop('disabled', true).text('Applying credits…');
                fetch(RESOLVE_URL + '&slug=' + encodeURIComponent(this.slug) + '&action=apply_credits', {
                    method: 'POST',
                    credentials: 'omit',
                    cache: 'no-store',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: this._customerBodyFor(sel).substring(1)
                })
                .then(function (response) {
                    if (!response.ok) throw new Error('HTTP ' + response.status);
                    return response.json();
                })
                .then(function (data) {
                    if (!data || !data.ok) throw new Error((data && data.error) || 'apply_credits_failed');
                    self.creditsAppliedCents = data.appliedCents || sel.creditCents;
                    self.state = 'credits-applied';
                    self.render();
                })
                .catch(function (e) {
                    $btn.prop('disabled', false).text('Apply Credits');
                    $err.text((e && e.message) || 'Could not apply credits.').show();
                });
            },

            // Tell the resolve Suitelet to call stripeLib.processIntent for
            // this PI — matches the existing customer-pay Suitelet's inline
            // behavior. Webhook + poll are still the canonical retry paths;
            // this just shortens the gap between "Stripe says succeeded" and
            // "NetSuite record exists" from "next webhook" to "now."
            //
            // Fire-and-forget — failures are logged to console only; the
            // record will still get created by webhook/poll.
            _triggerInlineProcessing: function (intentId, onStatus) {
                if (!this.slug || !intentId) return;
                var url = RESOLVE_URL
                    + '&slug=' + encodeURIComponent(this.slug)
                    + '&action=process_intent';
                var body = 'payment_intent_id=' + encodeURIComponent(intentId);
                fetch(url, {
                    method: 'POST',
                    credentials: 'omit',
                    cache: 'no-store',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: body
                })
                .then(function (r) { return r.json(); })
                .then(function (data) {
                    if (data && data.ok) {
                        try { console.log('[ClemtexPay] inline processIntent ok', data); } catch (_) {}
                        // data.status is the PI's authoritative status —
                        // callers use it to correct the provisional outcome
                        // page rendered from Stripe's redirect_status flag.
                        if (onStatus && data.status) {
                            try { onStatus(String(data.status), data); } catch (_) {}
                        }
                    } else {
                        try { console.warn('[ClemtexPay] inline processIntent skipped/failed (webhook will retry)', data); } catch (_) {}
                    }
                })
                .catch(function (e) {
                    try { console.warn('[ClemtexPay] inline processIntent network error (webhook will retry)', e); } catch (_) {}
                });
            },

            _submitPo: function (poNumber, pdfBase64, pdfFileName, onSuccess, onError) {
                var url = RESOLVE_URL
                    + '&slug=' + encodeURIComponent(this.slug)
                    + '&action=submit_po';
                var body = 'po_number=' + encodeURIComponent(poNumber)
                    + '&pdf_base64=' + encodeURIComponent(pdfBase64)
                    + '&pdf_file_name=' + encodeURIComponent(pdfFileName);
                // application/x-www-form-urlencoded is a CORS-safe Content-Type
                // (no preflight). Same reason the GET uses fetch with no headers.
                fetch(url, {
                    method: 'POST',
                    credentials: 'omit',
                    cache: 'no-store',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: body
                })
                .then(function (response) {
                    if (!response.ok) throw new Error('HTTP ' + response.status);
                    return response.json();
                })
                .then(function (data) {
                    if (!data || !data.ok) {
                        return onError((data && data.error) || 'Submission failed.');
                    }
                    onSuccess(data);
                })
                .catch(function (e) {
                    onError((e && e.message) || 'Network error.');
                });
            }
        });

        return { PayHostedView: PayHostedView };
    }
);
